import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { getStartDate } from './CostChart';

const PROVIDER_COLORS = {
  aws: '#FF9900',
  azure: '#007FFF',
  gcp: '#4285F4',
  ovh: '#123F6D'
};

const PROVIDER_NAMES = {
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  ovh: 'OVH'
};

const RANGE_LABELS = {
  '1m': '1 mois',
  '3m': '3 mois',
  '6m': '6 mois',
  '12m': '12 mois',
  '24m': '24 mois',
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-popover/95 backdrop-blur-sm border border-border shadow-xl rounded-lg p-3 text-sm">
        <p className="font-medium">{PROVIDER_NAMES[data.provider]}</p>
        <p className="text-muted-foreground">
          {data.cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € ({data.percentage}%)
        </p>
      </div>
    );
  }
  return null;
};

export const ProviderBreakdown = ({ data, range }) => {
  const startDate = getStartDate(range);
  const startStr = startDate.toISOString().slice(0, 10);

  const providerCosts = {};
  (data?.providers || []).forEach(p => {
    let cost = 0;
    (p.cost_by_day || []).forEach(d => {
      if (d.date >= startStr) {
        cost += d.cost;
      }
    });
    if (cost > 0) {
      providerCosts[p.provider] = cost;
    }
  });

  const totalCost = Object.values(providerCosts).reduce((sum, c) => sum + c, 0);

  const chartData = Object.entries(providerCosts).map(([provider, cost]) => ({
    provider,
    cost,
    percentage: totalCost > 0 ? ((cost / totalCost) * 100).toFixed(1) : '0'
  }));

  return (
    <Card data-testid="provider-breakdown" className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-lg font-[Outfit]">Répartition par Provider</CardTitle>
        <p className="text-xs text-muted-foreground">{RANGE_LABELS[range] || range}</p>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col justify-center">
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[250px]">
            <p className="text-muted-foreground text-sm">Aucune donnée sur cette période.</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="cost"
                  nameKey="provider"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PROVIDER_COLORS[entry.provider]}
                      stroke="transparent"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={(value) => PROVIDER_NAMES[value] || value}
                  wrapperStyle={{ fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {chartData.map(item => (
                <div key={item.provider} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: PROVIDER_COLORS[item.provider] }}
                    />
                    <span>{PROVIDER_NAMES[item.provider]}</span>
                  </div>
                  <span className="font-medium">{item.cost.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
